import React from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';

import formatCurrency from '../../../utils/formatCurrency';
import { formatDepositPromotion } from '../../../utils/dateFormatters';
import DraftstarsLogo from './draftstars-logo-inverted.png';
import PlayUpLogo from './playup-logo-inverted.svg';

enum PromotionPlatform {
	PlayUp = 1,
	Draftstars = 2,
}

const DepositPromotions: React.FC<Props> = ({ depositPromotions, selectedPromotionId, selectPromotion }) => {
	if (depositPromotions.length === 0) return null;

	const getBrandName = (promotionId: number) => {
		if (promotionId === PromotionPlatform.PlayUp) {
			return 'PlayUp';
		}
		if (promotionId === PromotionPlatform.Draftstars) {
			return 'Draftstars';
		}
		return null;
	};

	const renderPromotionMessage = (promotion: DepositPromotion) => {
		const brandName = getBrandName(promotion.client.id);

		let message = `Max${brandName && ' ' + brandName} bonus: ${formatCurrency(promotion.rules.max_bonus_amount)}. `;
		if (brandName) {
			message += `Redeemable only on ${brandName}. `;
		}
		if (!promotion.rules?.never_expire && promotion.expired_at) {
			message += `Valid until ${formatDepositPromotion(dayjs(promotion.expired_at))}`;
		}
		return message;
	};

	const renderBrandLogo = (promotionId: number) => {
		if (promotionId === PromotionPlatform.PlayUp) {
			return <BrandLogo src={PlayUpLogo} />;
		}
		if (promotionId === PromotionPlatform.Draftstars) {
			return <BrandLogo src={DraftstarsLogo} />;
		}
		return null;
	};

	return (
		<Wrapper>
			<Title>Apply deposit promotion</Title>
			<ul>
				{depositPromotions.map((promotion) => (
					<PromotionWrapper
						key={promotion.id}
						onClick={() => selectPromotion(promotion.id)}
						selected={selectedPromotionId === promotion.id}
					>
						<LeftWrapper>
							{renderBrandLogo(promotion.client.id)}
							<PromotionDescription>
								<PromotionTitle>{promotion.name}</PromotionTitle>
								<PromotionBody>{renderPromotionMessage(promotion)}</PromotionBody>
							</PromotionDescription>
						</LeftWrapper>
						<RightWrapper>
							<RadioButton type="radio" checked={selectedPromotionId === promotion.id} readOnly />
						</RightWrapper>
					</PromotionWrapper>
				))}
			</ul>
		</Wrapper>
	);
};

export default DepositPromotions;

interface Props {
	depositPromotions: Array<DepositPromotion>;
	selectedPromotionId?: string;
	selectPromotion: (promotionId: string) => void;
}

const Wrapper = styled.div`
	margin-bottom: 1rem;
	color: #000;
`;

const Title = styled.p`
	line-height: 1.5;
	margin-bottom: 0.5rem;
`;

const PromotionWrapper = styled.li<{ selected: boolean }>`
	display: flex;
	align-items: center;
	margin-bottom: 0.25rem;
	cursor: pointer;
	background-color: ${({ selected }) => (selected ? '#e2e8f0' : '#edf2f7')};
	padding: 0.5em 1rem;
	border-radius: 0.25rem;
	&:hover {
		background-color: #e2e8f0;
	}
	transition: 100ms;
`;

const LeftWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
`;

const RightWrapper = styled.div`
	display: flex;
`;

const PromotionTitle = styled.p`
	font-weight: 600;
	font-size: 1.1rem;
	margin-bottom: 0.25rem;
`;

const PromotionBody = styled.p`
	font-size: 0.8em;
`;

const PromotionDescription = styled.div`
	flex-grow: 1;
	padding-right: 1em;
`;

const RadioButton = styled.input`
	cursor: pointer;
`;

const BrandLogo = styled.img`
	height: 15px;
	margin-bottom: 5px;
`;

// TODO: this should be moved to a helper file

// * This method crates a dummy deposit promotion object that should only be used for visual testing purposes. This could bork API calls.

const createDummyDepositPromotion = (numberGenned: number) => {
	const promotionClientNumber = Math.random() > 0.5 ? 1 : 2;
	const newDepositPromo = {
		id: `${numberGenned}`,
		name: 'Test Promotion' + numberGenned,
		rules: {
			max_bonus_amount: 100,
			never_expire: false,
			percentage: 100,
			valid_days: 7,
		},
		client: {
			id: promotionClientNumber,
			name: promotionClientNumber === 1 ? 'PlayUp' : 'Draftstars',
		},
		expired_at: new Date().setDate(new Date().getDate() + 7).toString(),
		code: 'TESTCODE' + numberGenned,
	} as DepositPromotion;
	return newDepositPromo;
};

const createPromotions = (numberOfPromotions: number) => {
	const promos = [] as Array<DepositPromotion>;
	for (let i = 0; i < numberOfPromotions; i++) {
		promos.push(createDummyDepositPromotion(i));
	}
	return promos;
};
